import { CustomToastComponent } from "@/components/shared";
import {
  FetchDocumentsByTenderIdDocument,
  FetchDocumentsByTenderIdQuery,
  FetchDocumentsByTenderIdQueryVariables,
  RemoveDocumentFromTenderDocument,
  RemoveDocumentFromTenderMutation,
  RemoveDocumentFromTenderMutationVariables,
} from "@/graphql/__generated__/graphql";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

/**
 * Custom hook for removing a document from a tender.
 * @returns {Object} - Contains the removeDocumentFromTenderMutation function, loading state, and error state.
 */
export default function useRemoveDocumentFromTenderMutationHook(): {
  removeDocumentFromTenderMutation: any;
  loading: any;
  error: any;
  } {
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [removeDocumentFromTenderMutation, { loading, error }] = useMutation<RemoveDocumentFromTenderMutation, RemoveDocumentFromTenderMutationVariables>(
    RemoveDocumentFromTenderDocument, {
      update: (cache, { data }) => {
        const documentId = data?.removeDocumentFromTender?.id;
        if (!documentId) return;

        // Read the current documents from cache
        const existingData = cache.readQuery<
        FetchDocumentsByTenderIdQuery,
        FetchDocumentsByTenderIdQueryVariables
      >({
        query: FetchDocumentsByTenderIdDocument,
        variables: { tenderId: Number(tenderID) },
      });

        if (existingData?.fetchDocumentsByTenderId) {
        // Remove the document from the list
          const updatedDocuments = existingData.fetchDocumentsByTenderId.filter(
            (doc) => doc.id !== documentId
          );

          // Write the updated documents back to cache
          cache.writeQuery<
          FetchDocumentsByTenderIdQuery,
          FetchDocumentsByTenderIdQueryVariables
        >({
          query: FetchDocumentsByTenderIdDocument,
          variables: { tenderId: Number(tenderID) },
          data: {
            fetchDocumentsByTenderId: updatedDocuments,
          },
        });
        }
      },
      onCompleted: () => {
        toast(CustomToastComponent, {
          toastId: "document-removed-success",
          data: {
            title: t("toasts.documentremovedsuccessfully"),
            type: "success",
          },
        });
      },
      refetchQueries: [
        {
          query: FetchDocumentsByTenderIdDocument,
          variables: { tenderId: Number(tenderID) },
        },
      ],
    });

  return {
    removeDocumentFromTenderMutation,
    loading,
    error,
  };
}
